<template>
  <div
    ref="root"
    role="group"
    :class="[
      $style.callout,
      computedLayout === 'multiline' && $style.callout_multiline,
      variant === 'experimental'
        ? [
            $style.callout_experimental,
            'bg-yellow-400/10 dark:bg-yellow-900/25',
          ]
        : tone === 'alert'
        ? 'bg-amber-100 dark:bg-amber-950/50'
        : 'bg-card',
    ]"
    class="shrink-0 py-2"
  >
    <div
      :class="$style.icon"
      class="my-1 grid h-5 w-4 shrink-0 place-items-center"
      aria-hidden="true"
    >
      <ArrowPathIcon
        v-if="loading"
        class="size-4 animate-spin text-muted-foreground"
      />
      <InformationCircleIcon
        v-else-if="variant === 'info'"
        class="size-4 text-muted-foreground"
      />
      <CheckCircleIcon
        v-else-if="variant === 'success'"
        class="size-4 text-green-500"
      />
      <ExclamationTriangleIcon
        v-else-if="variant === 'warning'"
        class="size-4 text-yellow-600 dark:text-yellow-500"
      />
      <ExclamationCircleIcon
        v-else-if="variant === 'error'"
        class="size-4 text-red-400"
      />
      <ExclamationTriangleIcon
        v-else-if="variant === 'experimental'"
        class="size-4 text-yellow-600 dark:text-yellow-500"
      />
    </div>

    <div :class="$style.body" class="my-1 text-sm">
      <span v-if="slots.title" class="font-bold">
        <slot name="title"></slot>
      </span>

      <slot />
    </div>

    <div
      v-if="slots.actions"
      :class="$style.actions"
      class="flex h-7 items-center gap-2.5"
    >
      <slot name="actions"></slot>
    </div>

    <div v-if="onClose" :class="$style.close" class="my-1">
      <button
        type="button"
        aria-label="Dismiss message"
        class="grid size-5 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground active:bg-muted active:text-foreground"
        @click="onClose"
      >
        <XMarkIcon class="size-4" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onWatcherCleanup, ref, useTemplateRef, watch } from "vue";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckCircleIcon from "@/icons/heroicons/check-circle-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";
import InformationCircleIcon from "@/icons/heroicons/information-circle-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";

const props = withDefaults(
  defineProps<{
    variant?: "error" | "experimental" | "info" | "success" | "warning";
    layout?: "auto" | "multiline" | "singleline";
    loading?: boolean;
    tone?: "alert" | "default";
    onClose?: () => void;
  }>(),
  {
    variant: "info",
    layout: "auto",
    loading: false,
    tone: "default",
  }
);

const slots = defineSlots<{
  /** Callout message */
  default(props: {}): unknown;
  /** Short descriptive title */
  title?(props: {}): unknown;
  /** Available actions */
  actions?(props: {}): unknown;
}>();

const root = useTemplateRef("root");
const reflowing = ref(false);

watch([root, () => props.layout], ([el, layout]) => {
  if (!el || layout !== "auto") {
    return;
  }

  let singlelineWidth = 0;
  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    const inlineSize = entry.borderBoxSize[0].inlineSize;

    if (reflowing.value) {
      if (singlelineWidth > 0 && inlineSize >= singlelineWidth) {
        reflowing.value = false;
      }
    } else {
      singlelineWidth = entry.target.scrollWidth;
      if (singlelineWidth > Math.ceil(inlineSize)) {
        reflowing.value = true;
      }
    }
  });

  observer.observe(el, { box: "border-box" });
  onWatcherCleanup(() => observer.disconnect());
});

const computedLayout = computed(() => {
  if (props.layout === "auto") {
    return reflowing.value ? "multiline" : "singleline";
  }

  return props.layout;
});
</script>

<style module>
/* The auto-layout detection compares scrollWidth against the container's
   inline size, so the grid must be styled to keep scrollWidth accurate:
   - no column-gap (gap is excluded from scrollWidth); spacing is done
     with padding/margin on children instead
   - 1fr instead of minmax(0, 1fr) so the body column's min-width:auto
     lets scrollWidth grow when content overflows, triggering the reflow */
.callout {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  align-items: start;
  row-gap: 0.25rem;
  padding-left: 1rem;
  white-space: nowrap;

  .icon {
    margin-right: 0.75rem;
  }

  .body {
    padding-right: 1rem;
  }

  .actions {
    padding-right: 1rem;
  }

  .close {
    padding-right: 1rem;
  }

  .actions:has(+ .close) {
    padding-right: 0.5rem;
  }
}

.callout_experimental {
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.05),
    rgba(0, 0, 0, 0.05) 20px,
    transparent 20px,
    transparent 40px
  );

  :global(.dark) & {
    background-image: repeating-linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.025),
      rgba(255, 255, 255, 0.025) 20px,
      transparent 20px,
      transparent 40px
    );
  }
}

.callout_multiline {
  grid-auto-flow: row;
  grid-template-columns: auto 1fr auto;
  white-space: normal;

  .icon {
    grid-row: 1;
    grid-column: 1;
  }

  .body {
    grid-row: 1;
    grid-column: 2;
  }

  .close {
    grid-row: 1;
    grid-column: 3;
  }

  .actions {
    grid-row: 2;
    grid-column: 2 / -1;
    justify-content: end;
  }
}
</style>
